/**
 * Enhanced mobile tracking for authenticated users
 * This module adds comprehensive tracking for mobile users who are already in the app
 */

import appInsightsService from './applicationInsights';

class MobileEnhancedTracking {
  constructor() {
    this.isInitialized = false;
    this.originalFetch = null;
    this.originalXHR = null;
  }

  /**
   * Initialize enhanced tracking for mobile
   * Call this AFTER user is authenticated
   */
  initialize() {
    if (this.isInitialized) return;
    
    const isMobile = /TeamsMobile|Android.*Teams|iPhone.*Teams|iPad.*Teams/i.test(navigator.userAgent);
    if (!isMobile) return;
    
    // Track that enhanced tracking is starting (before console override)
    appInsightsService.trackEvent('MobileEnhancedTrackingInitialized', {
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString()
    });
    
    console.log('📱 Initializing enhanced mobile tracking');
    
    // Override console methods
    this.overrideConsole();
    
    // Track API calls
    this.interceptFetch();
    this.interceptXHR();
    
    // Track navigation
    this.trackNavigation();
    
    // Track errors
    this.trackErrors();
    
    this.isInitialized = true;
  }
  
  /**
   * Override console methods to capture all logs
   */
  overrideConsole() {
    const methods = ['log', 'error', 'warn', 'info', 'debug'];
    const originalConsole = {};
    
    methods.forEach(method => {
      originalConsole[method] = console[method];
      
      console[method] = (...args) => {
        // Call original method
        originalConsole[method](...args);
        
        // CRITICAL FIX: Prevent infinite loop by checking if this is an App Insights log
        const message = args.map(arg => {
          if (typeof arg === 'object') {
            try {
              return JSON.stringify(arg, null, 2);
            } catch (e) {
              return '[Object]';
            }
          }
          return String(arg);
        }).join(' ');
        
        // Skip tracking if this is an App Insights log itself
        if (message.includes('[AppInsights]') || 
            message.includes('[MobileTelemetry]') ||
            message.includes('MobileConsoleLog')) {
          return;
        }
        
        // Track to App Insights
        try {
          appInsightsService.trackEvent('MobileConsoleLog', {
            level: method,
            message: message.substring(0, 2000), // Limit size
            timestamp: new Date().toISOString(),
            url: window.location.href,
            stack: method === 'error' ? new Error().stack : undefined
          });
        } catch (e) {
          // Silently fail to not break console
        }
      };
    });
    
    // Use original console to avoid tracking this message
    originalConsole.log('✅ Console methods overridden for mobile tracking');
  }
  
  /**
   * Intercept fetch calls
   */
  interceptFetch() {
    this.originalFetch = window.fetch;
    
    window.fetch = async (...args) => {
      const [url, options = {}] = args;
      const method = options.method || 'GET';
      const startTime = Date.now();
      
      // CRITICAL FIX: Don't track App Insights requests to prevent infinite loop
      const urlString = String(url).toLowerCase();
      const isAppInsightsRequest = urlString.includes('applicationinsights.azure.com') ||
                                   urlString.includes('applicationinsights.microsoft.com') ||
                                   urlString.includes('services.visualstudio.com') ||
                                   urlString.includes('in.applicationinsights') ||
                                   urlString.includes('live.applicationinsights') ||
                                   urlString.includes('monitor.azure.com') ||
                                   urlString.includes('/v2/track');
      
      if (!isAppInsightsRequest) {
        // Track request start (only non-App Insights)
        appInsightsService.trackEvent('MobileApiRequest', {
          url: String(url),
          method,
          timestamp: new Date().toISOString(),
          body: options.body ? String(options.body).substring(0, 500) : undefined
        });
      }
      
      try {
        const response = await this.originalFetch(...args);
        const duration = Date.now() - startTime;
        
        // Clone response to read body
        const clonedResponse = response.clone();
        let responseBody = null;
        
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            responseBody = await clonedResponse.json();
          }
        } catch (e) {
          // Ignore body parsing errors
        }
        
        // Track response (only non-App Insights)
        if (!isAppInsightsRequest) {
          appInsightsService.trackEvent('MobileApiResponse', {
            url: String(url),
            method,
            status: response.status,
            statusText: response.statusText,
            duration,
            success: response.ok,
            responseBody: responseBody ? JSON.stringify(responseBody).substring(0, 500) : undefined,
            timestamp: new Date().toISOString()
          });
          
          // Track errors specifically
          if (!response.ok) {
            appInsightsService.trackEvent('MobileApiError', {
              url: String(url),
              method,
              status: response.status,
              statusText: response.statusText,
              duration,
              responseBody: responseBody ? JSON.stringify(responseBody) : undefined,
              timestamp: new Date().toISOString()
            });
          }
        }
        
        return response;
      } catch (error) {
        const duration = Date.now() - startTime;
        
        // Track network errors (only non-App Insights)
        if (!isAppInsightsRequest) {
          appInsightsService.trackEvent('MobileApiNetworkError', {
            url: String(url),
            method,
            duration,
            errorMessage: error.message,
            errorType: error.name,
            errorStack: error.stack,
            timestamp: new Date().toISOString()
          });
        }
        
        throw error;
      }
    };
  }
  
  /**
   * Intercept XMLHttpRequest
   */
  interceptXHR() {
    const XHR = XMLHttpRequest.prototype;
    const originalOpen = XHR.open;
    const originalSend = XHR.send;
    
    XHR.open = function(method, url) {
      this._method = method;
      this._url = url;
      this._startTime = Date.now();
      return originalOpen.apply(this, arguments);
    };
    
    XHR.send = function(body) {
      // CRITICAL FIX: Don't track App Insights requests to prevent infinite loop
      const urlString = this._url ? String(this._url).toLowerCase() : '';
      const isAppInsightsRequest = urlString && (
        urlString.includes('applicationinsights.azure.com') ||
        urlString.includes('applicationinsights.microsoft.com') ||
        urlString.includes('services.visualstudio.com') ||
        urlString.includes('in.applicationinsights') ||
        urlString.includes('live.applicationinsights') ||
        urlString.includes('monitor.azure.com') ||
        urlString.includes('/v2/track')
      );
      
      if (!isAppInsightsRequest) {
        // Track request (only non-App Insights)
        appInsightsService.trackEvent('MobileXHRRequest', {
          url: this._url,
          method: this._method,
          timestamp: new Date().toISOString(),
          body: body ? String(body).substring(0, 500) : undefined
        });
      }
      
      // Add listeners
      this.addEventListener('load', function() {
        if (!isAppInsightsRequest) {
          const duration = Date.now() - this._startTime;
          
          appInsightsService.trackEvent('MobileXHRResponse', {
            url: this._url,
            method: this._method,
            status: this.status,
            statusText: this.statusText,
            duration,
            success: this.status >= 200 && this.status < 300,
            responseText: this.responseText ? this.responseText.substring(0, 500) : undefined,
            timestamp: new Date().toISOString()
          });
        }
      });
      
      this.addEventListener('error', function() {
        if (!isAppInsightsRequest) {
          const duration = Date.now() - this._startTime;
          
          appInsightsService.trackEvent('MobileXHRError', {
            url: this._url,
            method: this._method,
            duration,
            timestamp: new Date().toISOString()
          });
        }
      });
      
      return originalSend.apply(this, arguments);
    };
  }
  
  /**
   * Track navigation changes
   */
  trackNavigation() {
    // Track hash changes (React Router)
    window.addEventListener('hashchange', (event) => {
      appInsightsService.trackEvent('MobileNavigation', {
        from: event.oldURL,
        to: event.newURL,
        timestamp: new Date().toISOString()
      });
    });
    
    // Track popstate (browser back/forward)
    window.addEventListener('popstate', (event) => {
      appInsightsService.trackEvent('MobilePopState', {
        url: window.location.href,
        timestamp: new Date().toISOString()
      });
    });
  }
  
  /**
   * Track global errors
   */
  trackErrors() {
    window.addEventListener('error', (event) => {
      appInsightsService.trackEvent('MobileGlobalError', {
        message: event.message,
        filename: event.filename,
        line: event.lineno,
        column: event.colno,
        error: event.error ? event.error.stack : 'No stack trace',
        timestamp: new Date().toISOString(),
        url: window.location.href
      });
    });
    
    window.addEventListener('unhandledrejection', (event) => {
      appInsightsService.trackEvent('MobileUnhandledRejection', {
        reason: event.reason ? String(event.reason) : 'Unknown',
        promise: String(event.promise),
        timestamp: new Date().toISOString(),
        url: window.location.href
      });
    });
  }
}

// Create singleton instance
const mobileEnhancedTracking = new MobileEnhancedTracking();
export default mobileEnhancedTracking;